import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import {
  useGetDocuments,
  useCreateDocument,
  useUpdateDocument,
  useDeleteDocument,
} from "api/documents/service";
import StatesInput from "components/StatesInput";
import Select from "components/Select";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { FaPlus, FaEye } from "react-icons/fa";
import { MdEdit, MdClose, MdDelete } from "react-icons/md";

const Documents = ({ activeColor = "secondary" }) => {
  const { t } = useTranslation();
  const { data: documentsData, isLoading } = useGetDocuments();
  const createDocumentMutation = useCreateDocument();
  const updateDocumentMutation = useUpdateDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [viewingPdfIndex, setViewingPdfIndex] = useState(null);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [currentDocument, setCurrentDocument] = useState({
    name: "",
    type: "",
    state: null,
  });

  const documents = documentsData?.data || [];

  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // PDF Viewer plugin
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const getBackgroundColor = () => {
    return isLightTheme ? "#ffffff" : "#1e1e2f";
  };

  const getTextColor = () => {
    return isLightTheme ? "#000000" : "#ffffff";
  };

  const getBorderColor = () => {
    return isLightTheme ? "#dee2e6" : "rgba(255, 255, 255, 0.1)";
  };

  const getMutedTextColor = () => {
    return isLightTheme ? "#6c757d" : "rgba(255, 255, 255, 0.6)";
  };

  const typeOptions = [
    { value: "funded", label: "FUNDED" },
    { value: "optional", label: "OPTIONAL" },
    { value: "dmv", label: "DMV" },
    { value: "valuation", label: "VALUATION" },
  ];

  const handleAddClick = () => {
    setShowAddForm(true);
    setEditingIndex(null);
    setViewingPdfIndex(null);
    setViewingPdfUrl(null);
    setSelectedFile(null);
    setCurrentDocument({
      name: "",
      type: "",
      state: null,
    });
  };

  const handleEditClick = (index, e) => {
    if (e) {
      e.stopPropagation();
    }
    const doc = documents[index];
    setEditingIndex(editingIndex === index ? null : index);
    setShowAddForm(false);
    setViewingPdfIndex(null);
    setViewingPdfUrl(null);
    setSelectedFile(null);
    setCurrentDocument({
      name: doc.name || "",
      type: doc.type || "",
      state: doc.state || null,
    });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingIndex(null);
    setViewingPdfIndex(null);
    setViewingPdfUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setCurrentDocument({
      name: "",
      type: "",
      state: null,
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        alert(t("documents.onlyPdfAllowed"));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    if (!currentDocument.name || !currentDocument.type) {
      return;
    }

    // For new documents, PDF is required
    if (editingIndex === null && !selectedFile) {
      alert(t("documents.pdfRequired"));
      return;
    }

    const data = {
      name: currentDocument.name,
      type: currentDocument.type,
      state: currentDocument.state ? currentDocument.state.id : null,
    };

    // Prepare files object if file is selected
    const files = selectedFile ? { document: selectedFile } : null;

    if (editingIndex !== null) {
      // Update
      const docId = documents[editingIndex].doc_id;
      updateDocumentMutation.mutate(
        { id: docId, data, files },
        {
          onSuccess: () => {
            handleCancel();
          },
        }
      );
    } else {
      // Create - files are required
      createDocumentMutation.mutate(
        { data, files },
        {
          onSuccess: () => {
            handleCancel();
          },
        }
      );
    }
  };

  const getDocumentUrl = (doc) => {
    let url = null;
    if (doc.document?.url) {
      url = doc.document.url;
    } else if (doc.document?.data?.attributes?.url) {
      url = doc.document.data.attributes.url;
    } else if (doc.url) {
      url = doc.url;
    } else if (doc.documentId) {
      url = `${process.env.REACT_APP_API_URL || ""}upload/files/${
        doc.documentId
      }`;
    }

    if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
      url = `${process.env.REACT_APP_PHOTO_URL || ""}${
        url.startsWith("/") ? url : "/" + url
      }`;
    }

    return url;
  };

  const handleViewDocument = (doc, index, e) => {
    if (e) {
      e.stopPropagation();
    }
    const url = getDocumentUrl(doc);
    if (url) {
      setViewingPdfIndex(viewingPdfIndex === index ? null : index);
      setViewingPdfUrl(viewingPdfIndex === index ? null : url);
      setEditingIndex(null);
      setShowAddForm(false);
    }
  };

  const handleClosePdf = () => {
    setViewingPdfIndex(null);
    setViewingPdfUrl(null);
  };

  const handleDeleteClick = (index, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteIndex !== null) {
      const docId = documents[selectedDeleteIndex].doc_id;
      deleteDocumentMutation.mutate(docId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteIndex(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteIndex(null);
  };

  const customSelectStyles = useMemo(() => {
    return {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: getBackgroundColor(),
        color: getTextColor(),
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: getBackgroundColor(),
        color: getTextColor(),
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? activeColorStyle.background
          : state.isFocused
          ? isLightTheme
            ? "#f8f9fa"
            : "rgba(255, 255, 255, 0.1)"
          : "transparent",
        color: state.isSelected ? "white" : getTextColor(),
        "&:hover": {
          backgroundColor: state.isSelected
            ? activeColorStyle.background
            : isLightTheme
            ? "#f8f9fa"
            : "rgba(255, 255, 255, 0.1)",
        },
      }),
      singleValue: (provided) => ({
        ...provided,
        color: getTextColor(),
      }),
      input: (provided) => ({
        ...provided,
        color: getTextColor(),
      }),
      placeholder: (provided) => ({
        ...provided,
        color: getMutedTextColor(),
      }),
    };
  }, [
    isLightTheme,
    activeColorStyle,
    getBackgroundColor,
    getTextColor,
    getBorderColor,
    getMutedTextColor,
  ]);

  return (
    <div className="content" style={{ minHeight: "80vh", paddingTop: "5rem" }}>
      <div style={{ width: "100%" }}>
        <div className="mt-0 w-100">
              <Row>
                <Col md="12">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h4">{t("documents.title")}</CardTitle>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleAddClick}
                      style={activeColorStyle}
                    >
                      <FaPlus style={{ marginRight: "5px" }} />
                      {t("documents.add")}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {showAddForm && (
                    <Card
                      className="mb-4"
                      style={{
                        backgroundColor: getBackgroundColor(),
                        boxShadow: 'none',
                      }}
                    >
                    <CardBody>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label>
                                {t("documents.name")}{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Label>
                              <Input
                                type="text"
                                value={currentDocument.name}
                                onChange={(e) =>
                                  setCurrentDocument((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                                placeholder={t("documents.namePlaceholder")}
                                style={{ 
                                  backgroundColor: getBackgroundColor(),
                                  color: getTextColor(),
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>
                                {t("documents.pdfFile")}{" "}
                                {editingIndex === null && (
                                  <span style={{ color: "red" }}>*</span>
                                )}
                              </Label>
                              <div>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept=".pdf,application/pdf"
                                  onChange={handleFileSelect}
                                  style={{ display: "none" }}
                                />
                                {selectedFile ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <span style={{ color: getTextColor() }}>
                                      {selectedFile.name}
                                    </span>
                              <Button
                                      type="button"
                                      color="danger"
                                      size="sm"
                                      onClick={handleRemoveFile}
                                    >
                                      <MdClose />
                              </Button>
                            </div>
                                ) : (
                                  <div>
                                    {editingIndex !== null &&
                                      documents[editingIndex]?.document && (
                                        <div
                          style={{ 
                                            marginBottom: "10px",
                                            color: getMutedTextColor(),
                                            fontSize: "12px",
                                          }}
                                        >
                                          {t("documents.currentFile")}:{" "}
                                          {documents[editingIndex].document
                                            .name || "PDF"}
                                        </div>
                                      )}
                                    <Button
                                      type="button"
                                      color="secondary"
                                      size="sm"
                                      onClick={() =>
                                        fileInputRef.current?.click()
                                      }
                                    >
                                      {t("documents.selectPdf")}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label>
                                {t("documents.type")}{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Label>
                              <Select
                                activeColor={activeColor}
                                className="react-select"
                                placeholder={t("documents.typePlaceholder")}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={
                                  currentDocument.type
                                    ? {
                                        value: currentDocument.type,
                                        label:
                                          typeOptions.find(
                                            (opt) =>
                                              opt.value === currentDocument.type
                                          )?.label || currentDocument.type,
                                      }
                                    : null
                                }
                                onChange={(option) =>
                                  setCurrentDocument((prev) => ({
                                    ...prev,
                                    type: option?.value || "",
                                  }))
                                }
                                options={typeOptions}
                                customSelectStyles={customSelectStyles}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t("documents.state")}</Label>
                              <StatesInput
                                name="state"
                                value={currentDocument.state}
                                onChange={(stateObject) => {
                                  setCurrentDocument((prev) => ({
                                    ...prev,
                                    state: stateObject
                                      ? {
                                          id: stateObject.id,
                                          abbreviation:
                                            stateObject.abbreviation,
                                        }
                                      : null,
                                  }));
                                }}
                                customSelectStyles={customSelectStyles}
                                placeholder={t("documents.statePlaceholder")}
                                activeColor={activeColor}
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col className="text-right">
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleCancel}
                              style={{ marginRight: "10px" }}
                            >
                              {t("documents.cancel")}
                            </Button>
                                <Button
                                  size="sm"
                              style={{
                                ...activeColorStyle,
                                color: "white",
                              }}
                              onClick={handleSave}
                              disabled={
                                createDocumentMutation.isPending ||
                                updateDocumentMutation.isPending ||
                                !currentDocument.name ||
                                !currentDocument.type ||
                                (editingIndex === null && !selectedFile)
                              }
                            >
                              {createDocumentMutation.isPending ||
                              updateDocumentMutation.isPending
                                ? t("documents.uploading")
                                : editingIndex !== null
                                ? t("documents.update")
                                : t("documents.add")}
                                </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  )}

                  {isLoading ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: getTextColor(),
                      }}
                    >
                      {t("common.loading")}
                    </div>
                  ) : documents.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: getMutedTextColor(),
                      }}
                    >
                      {t("documents.noDocuments")}
                              </div>
                  ) : (
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>{t("documents.id")}</th>
                          <th>{t("documents.name")}</th>
                          <th>{t("documents.type")}</th>
                          <th>{t("documents.state")}</th>
                          <th
                            className="text-center"
                            style={{ width: "100px" }}
                          >
                            {t("documents.view")}
                          </th>
                          <th
                            className="text-center"
                            style={{ width: "150px" }}
                          >
                            {t("documents.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc, idx) => (
                          <React.Fragment key={idx}>
                            <tr>
                              <td>{doc.doc_id}</td>
                              <td>{doc.name || "-"}</td>
                              <td>
                                {doc.type
                                  ? typeOptions.find(
                                      (opt) => opt.value === doc.type
                                    )?.label || doc.type
                                  : "-"}
                              </td>
                              <td>{doc.state?.name || "-"}</td>
                              <td className="text-center">
                                {doc.document && (
                                  <button
                                    type="button"
                                    className="btn-icon btn-link like"
                                    style={{ padding: "0px 6px" }}
                                    onClick={(e) =>
                                      handleViewDocument(doc, idx, e)
                                    }
                                  >
                                    <FaEye
                                      style={{
                                        color: isLightTheme ? "#000" : "#fff",
                                      }}
                                    />
                                  </button>
                                )}
                              </td>
                              <td className="text-center">
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "10px",
                                    alignItems: "center",
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="btn-icon btn-link like"
                                    style={{ padding: "0px 6px" }}
                                    onClick={(e) => handleEditClick(idx, e)}
                                  >
                                    <MdEdit
                                      style={{
                                        color: isLightTheme ? "#000" : "#fff",
                                      }}
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon btn-link like"
                                    style={{ padding: "0px 6px" }}
                                    onClick={(e) => handleDeleteClick(idx, e)}
                                  >
                                    <MdDelete
                                      style={{
                                        color: isLightTheme ? "#000" : "#fff",
                                      }}
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {editingIndex === idx && (
                              <tr>
                                <td colSpan="6">
                                  <Card
                                    className="mb-0"
                                    style={{
                                      backgroundColor: getBackgroundColor(),
                                      boxShadow: 'none',
                                    }}
                                  >
                                    <CardBody>
                                      <Row>
                                        <Col md="6">
                                          <FormGroup>
                                            <Label>
                                              {t("documents.name")}{" "}
                                              <span style={{ color: "red" }}>
                                                *
                                              </span>
                                            </Label>
                                            <Input
                                              type="text"
                                              value={currentDocument.name}
                                              onChange={(e) =>
                                                setCurrentDocument((prev) => ({
                                                  ...prev,
                                                  name: e.target.value,
                                                }))
                                              }
                                              placeholder={t(
                                                "documents.namePlaceholder"
                                              )}
                                              style={{
                                                backgroundColor:
                                                  getBackgroundColor(),
                                                color: getTextColor(),
                                              }}
                                            />
                                          </FormGroup>
                                        </Col>
                                        <Col md="6">
                                          <FormGroup>
                                            <Label>
                                              {t("documents.pdfFile")}
                                            </Label>
                                            <div>
                                              <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".pdf,application/pdf"
                                                onChange={handleFileSelect}
                                                style={{ display: "none" }}
                                              />
                                              {selectedFile ? (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      color: getTextColor(),
                                                    }}
                                                  >
                                                    {selectedFile.name}
                                                  </span>
                                                  <Button
                                                    type="button"
                                                    color="danger"
                                                    size="sm"
                                                    onClick={handleRemoveFile}
                                                  >
                                                    <MdClose />
                                                  </Button>
                                                </div>
                                              ) : (
                                                <div>
                                                  {documents[idx]?.document && (
                                                    <div
                                                      style={{
                                                        marginBottom: "10px",
                                                        color:
                                                          getMutedTextColor(),
                                                        fontSize: "12px",
                                                      }}
                                                    >
                                                      {t(
                                                        "documents.currentFile"
                                                      )}
                                                      :{" "}
                                                      {documents[idx].document
                                                        .name || "PDF"}
                                                    </div>
                                                  )}
                                                  <Button
                                                    type="button"
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() =>
                                                      fileInputRef.current?.click()
                                                    }
                                                  >
                                                    {t("documents.selectPdf")}
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          </FormGroup>
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col md="6">
                                          <FormGroup>
                                            <Label>
                                              {t("documents.type")}{" "}
                                              <span style={{ color: "red" }}>
                                                *
                                              </span>
                                            </Label>
                                            <Select
                                              activeColor={activeColor}
                                              className="react-select"
                                              placeholder={t(
                                                "documents.typePlaceholder"
                                              )}
                                              isSearchable={false}
                                              closeMenuOnSelect={true}
                                              value={
                                                currentDocument.type
                                                  ? {
                                                      value:
                                                        currentDocument.type,
                                                      label:
                                                        typeOptions.find(
                                                          (opt) =>
                                                            opt.value ===
                                                            currentDocument.type
                                                        )?.label ||
                                                        currentDocument.type,
                                                    }
                                                  : null
                                              }
                                              onChange={(option) =>
                                                setCurrentDocument((prev) => ({
                                                  ...prev,
                                                  type: option?.value || "",
                                                }))
                                              }
                                              options={typeOptions}
                                              customSelectStyles={
                                                customSelectStyles
                                              }
                                            />
                                          </FormGroup>
                                        </Col>
                                        <Col md="6">
                                          <FormGroup>
                                            <Label>
                                              {t("documents.state")}
                                            </Label>
                                            <StatesInput
                                              name="state"
                                              value={currentDocument.state}
                                              onChange={(stateObject) => {
                                                setCurrentDocument((prev) => ({
                                                  ...prev,
                                                  state: stateObject
                                                    ? {
                                                        id: stateObject.id,
                                                        abbreviation:
                                                          stateObject.abbreviation,
                                                      }
                                                    : null,
                                                }));
                                              }}
                                              customSelectStyles={
                                                customSelectStyles
                                              }
                                              placeholder={t(
                                                "documents.statePlaceholder"
                                              )}
                                              activeColor={activeColor}
                                              isClearable
                                            />
                                          </FormGroup>
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col className="text-right">
                                          <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={handleCancel}
                                            style={{ marginRight: "10px" }}
                                          >
                                            {t("documents.cancel")}
                                          </Button>
                                <Button
                                  size="sm"
                                            style={{
                                              ...activeColorStyle,
                                              color: "white",
                                            }}
                                            onClick={handleSave}
                                            disabled={
                                              createDocumentMutation.isPending ||
                                              updateDocumentMutation.isPending ||
                                              !currentDocument.name ||
                                              !currentDocument.type
                                            }
                                          >
                                            {createDocumentMutation.isPending ||
                                            updateDocumentMutation.isPending
                                              ? t("documents.uploading")
                                              : t("documents.update")}
                                          </Button>
                                        </Col>
                                      </Row>
                                    </CardBody>
                                  </Card>
                                </td>
                              </tr>
                            )}
                            {viewingPdfIndex === idx && viewingPdfUrl && (
                              <tr>
                                <td colSpan="6">
                                  <div
                                    style={{
                                      padding: "0px",
                                      height: "700px",
                                      position: "relative",
                                      display: "flex",
                                      flexDirection: "column",
                                      overflow: "hidden",
                                      boxShadow: 'none',
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        paddingBottom: "10px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Label
                                        className="form-control-label"
                                        style={{ fontWeight: "bold" }}
                                      >
                                        {t("documents.documentViewer")}
                                      </Label>
                                      <Button
                                        type="button"
                                        color="secondary"
                                        size="sm"
                                        onClick={handleClosePdf}
                                      >
                                        <i
                                          className="tim-icons icon-simple-remove"
                                          style={{ marginRight: "5px" }}
                                        />
                                        {t("documents.close")}
                                </Button>
                              </div>
                                    <div
                                      style={{
                                        flex: 1,
                                        minHeight: 0,
                                        border:
                                          "1px solid rgba(255, 255, 255, 0.1)",
                                        overflow: "hidden",
                                        position: "relative",
                                      }}
                                    >
                                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                        <div
                                          style={{
                                            height: "100%",
                                            overflow: "auto",
                                          }}
                                        >
                                          <Viewer
                                            defaultScale={1.0}
                                            theme={
                                              isLightTheme ? "light" : "dark"
                                            }
                                            fileUrl={viewingPdfUrl}
                                            plugins={[
                                              defaultLayoutPluginInstance,
                                            ]}
                                          />
                                </div>
                                      </Worker>
                              </div>
                            </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  )}
                          </CardBody>
                      </Card>
                </Col>
              </Row>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("documents.deleteDocument")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("documents.deleteConfirmMessage")}</p>
          <p className="text-muted">{t("documents.deleteWarning")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" type="button" onClick={handleCancelDelete}>
            {t("documents.cancel")}
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteDocumentMutation.isPending}
          >
            {deleteDocumentMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                {t("documents.deleting")}
              </>
            ) : (
              t("documents.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Documents;
